import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link as ReactLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useLoginMutation } from "@/app/features/auth/authApiSlice";
import { useDispatch } from "react-redux";
import { setCredentials } from "@/app/features/auth/authSlice";
import { apiSlice } from "@/app/api/apiSlice";
import { delftBlue, flame, neutral } from "@/theme/colors";
import Roles from "@/constants/roles";
import logoContainerSvg from "@/assets/Logo Container.svg";

const LoginPage = () => {
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      submit: null,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .max(255)
        .required("Email is required"),
      password: Yup.string()
        .max(255)
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        const { data, error } = await login({
          email: values.email,
          password: values.password,
        });

        if (error) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: error?.data?.message || "Login failed" });
          helpers.setSubmitting(false);
          return;
        }

        // Handle the nested response structure: { success, message, data: { token, user } }
        const responseData = data?.data || data;
        const user = responseData?.user;
        const token = responseData?.token;

        if (!user || !token) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: "Invalid response from server" });
          helpers.setSubmitting(false);
          return;
        }

        // Validate role - only admin and super_admin can access admin panel
        const allowedRoles = [Roles.ADMIN, Roles.SUPER_ADMIN];
        if (!user.role || !allowedRoles.includes(user.role)) {
          helpers.setStatus({ success: false });
          helpers.setErrors({
            submit:
              "Access denied. Only administrators can access the admin panel. Users should use the user login.",
          });
          helpers.setSubmitting(false);
          return;
        }

        // Clear RTK Query cache to ensure fresh data for new user
        dispatch(apiSlice.util.resetApiState());
        
        dispatch(
          setCredentials({
            email: user.email,
            isAdmin:
              user.role === Roles.SUPER_ADMIN || user.role === Roles.ADMIN,
            token: token,
            name: user.username || user.fullName || user.email,
            role: user.role,
            companyId: user.companyId || null,
          })
        );
        navigate("/");
      } catch (err) {
        console.log(err);
        helpers.setStatus({ success: false });
        helpers.setErrors({
          submit: err.message || "An error occurred during login",
        });
        helpers.setSubmitting(false);
      }
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: "url('/images/Background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: { xs: 1.5, sm: 2, md: 2.5 },
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: "95%", md: "auto" },
          maxWidth: { xs: "100%", sm: "1200px" },
          maxHeight: { xs: "100%", sm: "90vh", md: "85vh" },
          borderRadius: { xs: 2, sm: 3 },
          display: "inline-flex",
          justifyContent: "flex-start",
          alignItems: "stretch",
          gap: 0,
          overflow: "hidden",
          backgroundColor: "white",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        {/* Left Side - Splash Screen */}
        <Box
          sx={{
            width: { xs: 0, md: 384 },
            minWidth: { md: 384 },
            display: { xs: "none", md: "inline-flex" },
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: delftBlue[500],
            alignSelf: "stretch",
            overflow: "hidden",
            padding: { md: 2 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              width: "100%",
              maxHeight: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              {/* Logo Container */}
              <Box
                component="img"
                src={logoContainerSvg}
                alt="OP DOX Logo"
                sx={{
                  width: { md: "62.5%", lg: "75%" },
                  maxWidth: { md: 240, lg: 288 },
                  height: "auto",
                  aspectRatio: "1 / 1",
                  flexShrink: 0,
                }}
              />

              {/* Description */}
              <Typography
                sx={{
                  width: "100%",
                  textAlign: "center",
                  color: delftBlue[50],
                  fontSize: { md: 16, lg: 18 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  paddingX: 2,
                }}
              >
                All safety documentation, activity pre-start forms, and incident
                reports.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Form Container */}
        <Box
          sx={{
            flex: 1,
            display: "inline-flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            padding: { xs: 2, sm: 3, md: 4 },
            backgroundColor: "white",
            overflowY: "auto",
            maxHeight: "100%",
            minWidth: { xs: "100%", md: 400 },
          }}
        >
          {/* Logo for Mobile */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
              width: "100%",
              backgroundColor: delftBlue[500],
              borderRadius: { xs: 2, sm: 2.5 },
              padding: { xs: 2, sm: 2.5 },
              marginBottom: { xs: 1.5, sm: 2 },
            }}
          >
            <Box
              component="img"
              src={logoContainerSvg}
              alt="OP DOX Logo"
              sx={{
                width: { xs: 120, sm: 150 },
                height: { xs: 120, sm: 150 },
                flexShrink: 0,
              }}
            />
            {/* Description for Mobile */}
            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                color: delftBlue[50],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                paddingX: { xs: 2, sm: 3 },
              }}
            >
              All safety documentation, activity pre-start forms, and incident
              reports.
            </Typography>
          </Box>
          {/* Form Title */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 384,
              display: "inline-flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 0.75,
              marginBottom: { xs: 0.5, sm: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: "1.5rem", sm: "1.875rem" },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              Welcome to OP DOX
            </Typography>
          </Box>

          {/* Form */}
          <Box
            component="form"
            noValidate
            onSubmit={formik.handleSubmit}
            sx={{
              paddingX: { xs: 0, sm: 0.75 },
              paddingY: { xs: 1, sm: 1.25 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 1.25, sm: 1.5 },
              width: "100%",
              maxWidth: 384,
            }}
          >
            {/* Email Input */}
            <Box
              sx={{
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  minWidth: { xs: "100%", sm: 384 },
                  maxWidth: { xs: "100%", sm: 384 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    alignSelf: "stretch",
                    paddingBottom: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    sx={{
                      alignSelf: "stretch",
                      color: delftBlue[500],
                      fontSize: 16,
                      fontFamily: "Audiowide, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                  >
                    Enter Email
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    position: "relative",
                    paddingBottom:
                      formik.touched.email && formik.errors.email ? 2.5 : 0,
                  }}
                >
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    placeholder="admin@abc.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!!(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    FormHelperTextProps={{
                      sx: {
                        position: "absolute",
                        top: "100%",
                        margin: 0,
                        marginTop: 0.5,
                        whiteSpace: "nowrap",
                      },
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: 56,
                        paddingLeft: 0.75,
                        paddingRight: 0.75,
                        paddingTop: 1,
                        paddingBottom: 1,
                        backgroundColor: "white",
                        borderRadius: 2,
                        outline: `1px ${neutral[100]} solid`,
                        outlineOffset: "-1px",
                        "& fieldset": {
                          border: "none",
                        },
                        "& input": {
                          color: delftBlue[500],
                          fontSize: 16,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          "&::placeholder": {
                            color: neutral[400],
                            opacity: 1,
                          },
                        },
                        "&.Mui-error": {
                          outline: `1px ${flame[500]} solid`,
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        marginTop: 0.5,
                        fontSize: 12,
                        fontFamily: "Montserrat, sans-serif",
                        color: flame[500],
                        position: "absolute",
                        top: "100%",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Password Input */}
            <Box
              sx={{
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  minWidth: { xs: "100%", sm: 384 },
                  maxWidth: { xs: "100%", sm: 384 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    alignSelf: "stretch",
                    paddingBottom: 0.5,
                    display: "inline-flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: delftBlue[500],
                      fontSize: 16,
                      fontFamily: "Audiowide, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                  >
                    Enter Password
                  </Typography>
                  <Typography
                    component={ReactLink}
                    to="/forgot-password"
                    sx={{
                      color: delftBlue[500],
                      fontSize: 12,
                      fontFamily: "Audiowide, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.5,
                      textDecoration: "underline",
                      cursor: "pointer",
                      "&:hover": {
                        color: delftBlue[400],
                      },
                    }}
                  >
                    Forgot password?
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    position: "relative",
                    paddingBottom:
                      formik.touched.password && formik.errors.password
                        ? 2.5
                        : 0,
                  }}
                >
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      !!(formik.touched.password && formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            sx={{
                              color: neutral[400],
                              "&:hover": {
                                color: delftBlue[500],
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: {
                        position: "absolute",
                        top: "100%",
                        margin: 0,
                        marginTop: 0.5,
                        whiteSpace: "nowrap",
                      },
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: 56,
                        paddingLeft: 0.75,
                        paddingRight: 0.75,
                        paddingTop: 1,
                        paddingBottom: 1,
                        backgroundColor: "white",
                        borderRadius: 2,
                        outline: `1px ${neutral[100]} solid`,
                        outlineOffset: "-1px",
                        "& fieldset": {
                          border: "none",
                        },
                        "& input": {
                          color: delftBlue[500],
                          fontSize: 16,
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          "&::placeholder": {
                            color: neutral[400],
                            opacity: 1,
                          },
                        },
                        "&.Mui-error": {
                          outline: `1px ${flame[500]} solid`,
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        marginTop: 0.5,
                        fontSize: 12,
                        fontFamily: "Montserrat, sans-serif",
                        color: flame[500],
                        position: "absolute",
                        top: "100%",
                        whiteSpace: "nowrap",
                      },
                      "& input": {
                        // existing styles...
                        "&::-ms-reveal": {
                          display: "none",
                        },
                        "&::-ms-clear": {
                          display: "none",
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Error Message */}
            {formik.errors.submit && (
              <Typography
                sx={{
                  color: flame[500],
                  fontSize: 14,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  alignSelf: "stretch",
                  marginTop: 1,
                }}
              >
                {formik.errors.submit}
              </Typography>
            )}

            {/* Login Button */}
            <Button
              fullWidth
              type="submit"
              disabled={isLoading}
              sx={{
                alignSelf: "stretch",
                height: 56,
                paddingX: 1,
                paddingY: 0.75,
                backgroundColor: flame[500],
                borderRadius: 2,
                outline: `1px ${flame[500]} solid`,
                outlineOffset: "-1px",
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.75,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: flame[600],
                },
                "&:disabled": {
                  backgroundColor: neutral[300],
                  outline: `1px ${neutral[300]} solid`,
                },
              }}
            >
              <Typography
                sx={{
                  color: "white",
                  fontSize: 16,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                }}
              >
                {isLoading ? "Logging in..." : "Log In"}
              </Typography>
            </Button>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 384,
              paddingTop: { xs: 1, sm: 1.5 },
              paddingBottom: { xs: 1, sm: 1.5 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                paddingX: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  alignSelf: "stretch",
                  textAlign: "center",
                  color: delftBlue[500],
                  fontSize: 14,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  lineHeight: 1.43,
                }}
              >
                By continuing, you agree to our{" "}
                <Link
                  href="/OPDOX Terms Of Use.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: delftBlue[500],
                    textDecoration: "underline",
                    cursor: "pointer",
                    "&:hover": {
                      color: delftBlue[400],
                    },
                  }}
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="/OPDOX Privacy Policy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: delftBlue[500],
                    textDecoration: "underline",
                    cursor: "pointer",
                    "&:hover": {
                      color: delftBlue[400],
                    },
                  }}
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
